import { useEffect, useMemo, useState } from 'react';
import { Header, Card, Button, Select, Input, Badge, DataTable, Column, StatCard, Modal, Textarea, ConfirmDialog, NameCell, LongText, useToast } from '../../shared/components';
import { TimerIcon, AuditIcon } from '../../shared/icons';
import { useCurrentUser } from '../../shared/auth/AuthProvider';
import { getTurnoGeoRepository } from '../../shared/services/turnoGeo/repositoryFactory';
import { Overtime, OvertimeStatus, OVERTIME_STATUS_LABEL, fmtMinutes } from '../../shared/services/turnoGeo/types';

const repo = getTurnoGeoRepository();
const TONE: Record<OvertimeStatus, 'success' | 'warning' | 'critical' | 'info' | 'neutral'> = {
  DECLINADA: 'neutral', SOLICITADA: 'warning', APROBADA: 'success', RECHAZADA: 'critical', ENVIADA: 'info', PROCESADA: 'success',
};
const VIEWS = [
  { value: 'SOLICITADA', label: 'Por autorizar' },
  { value: 'APROBADA', label: 'Autorizadas (por enviar a RR.HH.)' },
  { value: 'ENVIADA', label: 'Enviadas a RR.HH.' },
  { value: 'PROCESADA', label: 'Procesadas por RR.HH.' },
  { value: 'RECHAZADA', label: 'Rechazadas' },
  { value: 'DECLINADA', label: 'El empleado dijo que no' },
  { value: '', label: 'Todas' },
];

/**
 * Horas extras (jefe, coordinador o autorizador). Aqui llegan las horas que el empleado confirmo al cerrar su jornada.
 * El jefe autoriza (todas las horas propuestas o menos) o rechaza con motivo, y luego envia lo autorizado a Recursos Humanos.
 */
export function TurnoGeoOvertimePage() {
  const toast = useToast();
  const me = useCurrentUser();
  const [view, setView] = useState('SOLICITADA');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [rows, setRows] = useState<Overtime[] | null>(null);
  const [counts, setCounts] = useState({ pending: 0, ready: 0 });
  const [reviewing, setReviewing] = useState<Overtime | null>(null);
  const [decision, setDecision] = useState<'APROBADA' | 'RECHAZADA'>('APROBADA');
  const [minutes, setMinutes] = useState(60);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);

  const load = async () => {
    setRows(null);
    try {
      const [list, pending, ready] = await Promise.all([
        repo.listOvertime({ status: view || undefined, dateFrom: dateFrom || undefined, dateTo: dateTo || undefined }),
        repo.listOvertime({ status: 'SOLICITADA' }),
        repo.listOvertime({ status: 'APROBADA' }),
      ]);
      setRows(list);
      setCounts({ pending: pending.length, ready: ready.length });
    } catch (e) { setRows([]); toast.show(e instanceof Error ? e.message : 'No se pudo cargar.', 'error'); }
  };
  useEffect(() => { void load(); }, [view, dateFrom, dateTo]);

  const open = (r: Overtime) => {
    setReviewing(r);
    setDecision(r.status === 'RECHAZADA' ? 'RECHAZADA' : 'APROBADA');
    setMinutes(r.approvedMinutes ?? r.proposedMinutes);
    setNote(r.reviewNote ?? '');
  };
  const save = async () => {
    if (!reviewing) return;
    setBusy(true);
    try {
      await repo.reviewOvertime(reviewing.id, decision === 'APROBADA' ? { decision, approvedMinutes: minutes, note: note.trim() || undefined } : { decision, note: note.trim() });
      toast.show(decision === 'APROBADA' ? 'Horas extras autorizadas.' : 'Horas extras rechazadas.');
      setReviewing(null);
      await load();
    } catch (e) { toast.show(e instanceof Error ? e.message : 'No se pudo guardar.', 'error'); }
    finally { setBusy(false); }
  };
  const send = async () => {
    setBusy(true);
    try {
      const r = await repo.sendOvertimeToHr();
      toast.show(r.sent > 0 ? `${r.sent} solicitud(es) enviada(s) a Recursos Humanos.` : 'No había horas autorizadas por enviar.');
      setConfirmSend(false);
      await load();
    } catch (e) { toast.show(e instanceof Error ? e.message : 'No se pudo enviar.', 'error'); }
    finally { setBusy(false); }
  };

  const minuteOptions = useMemo(() => {
    const max = reviewing?.proposedMinutes ?? 60;
    const out: { value: string; label: string }[] = [];
    for (let m = 60; m <= max; m += 60) out.push({ value: String(m), label: fmtMinutes(m) });
    return out;
  }, [reviewing]);

  const columns: Column<Overtime>[] = [
    { key: 'user', header: 'Empleado', filterValue: (r) => r.userFullName, sortable: true, sortValue: (r) => r.userFullName, render: (r) => <NameCell name={r.userFullName} /> },
    { key: 'date', header: 'Día', filterValue: (r) => r.workDate, sortable: true, sortValue: (r) => r.workDate, render: (r) => r.workDate },
    { key: 'sched', header: 'Jornada', render: (r) => fmtMinutes(r.scheduledMinutes) },
    { key: 'worked', header: 'Trabajado', render: (r) => fmtMinutes(r.workedMinutes) },
    { key: 'excess', header: 'Adicional', render: (r) => fmtMinutes(r.excessMinutes) },
    { key: 'prop', header: 'Propuesto', render: (r) => fmtMinutes(r.proposedMinutes) },
    { key: 'appr', header: 'Autorizado', render: (r) => (r.approvedMinutes != null ? fmtMinutes(r.approvedMinutes) : '—') },
    { key: 'status', header: 'Estado', filterValue: (r) => OVERTIME_STATUS_LABEL[r.status], render: (r) => <Badge tone={TONE[r.status]}>{OVERTIME_STATUS_LABEL[r.status]}</Badge> },
    { key: 'note', header: 'Lo que hizo el empleado', render: (r) => <LongText text={r.employeeNote} max={40} /> },
    { key: 'rev', header: 'Revisó', render: (r) => (r.reviewedBy ? `${r.reviewedBy}${r.reviewNote ? ` · ${r.reviewNote}` : ''}` : '—') },
    {
      key: 'act', header: '',
      render: (r) => (['SOLICITADA', 'APROBADA', 'RECHAZADA'] as OvertimeStatus[]).includes(r.status) && r.userId !== me.id
        ? <Button variant={r.status === 'SOLICITADA' ? 'primary' : 'outline'} size="md" onClick={() => open(r)}>{r.status === 'SOLICITADA' ? 'Revisar' : 'Cambiar'}</Button>
        : null,
    },
  ];

  return (
    <div>
      <Header title="Horas extras" subtitle="TurnoGeo" />
      <div className="space-y-4 p-4">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <StatCard icon={<TimerIcon />} label="Por autorizar" value={counts.pending} tone={counts.pending ? 'warning' : 'brand'} onClick={() => setView('SOLICITADA')} />
          <StatCard icon={<AuditIcon />} label="Autorizadas por enviar a RR.HH." value={counts.ready} tone={counts.ready ? 'info' : 'brand'} onClick={() => setView('APROBADA')} />
        </div>
        <Card>
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Select label="Ver" value={view} onChange={(e) => setView(e.target.value)} options={VIEWS} />
              <Input label="Desde" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              <Input label="Hasta" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={() => setConfirmSend(true)} disabled={counts.ready === 0}>Enviar horas autorizadas a Recursos Humanos ({counts.ready})</Button>
              <p className="text-xs text-ink-400">Solo se envían las horas que ya autorizaste. Recursos Humanos las verá en su pantalla de Horas extra.</p>
            </div>
          </div>
        </Card>
        <DataTable excel fixedKey="user" columns={columns} rows={rows ?? []} rowKey={(r) => r.id} isLoading={rows === null} emptyTitle="Sin horas extras en esta vista" emptyDescription="Cuando un empleado confirme horas extras al cerrar su jornada, aparecerán aquí." defaultSortKey="date" />
      </div>

      <Modal
        open={!!reviewing}
        onClose={() => setReviewing(null)}
        title="Autorizar horas extras"
        footer={<div className="flex gap-2"><Button variant="outline" fullWidth onClick={() => setReviewing(null)} disabled={busy}>Cancelar</Button><Button fullWidth onClick={save} isLoading={busy} disabled={decision === 'RECHAZADA' && note.trim().length < 5}>Guardar</Button></div>}
      >
        {reviewing && (
          <div className="space-y-3 text-sm text-ink-700">
            <p><b>{reviewing.userFullName}</b> · {reviewing.workDate}</p>
            <p>Jornada {fmtMinutes(reviewing.scheduledMinutes)} · trabajó {fmtMinutes(reviewing.workedMinutes)} · adicional {fmtMinutes(reviewing.excessMinutes)}. Propuesto: <b>{fmtMinutes(reviewing.proposedMinutes)}</b>.</p>
            <p className="rounded-xl bg-ink-50 p-3 text-xs">“{reviewing.employeeNote}”</p>
            <Select label="Decisión" value={decision} onChange={(e) => setDecision(e.target.value as 'APROBADA' | 'RECHAZADA')} options={[{ value: 'APROBADA', label: 'Autorizar' }, { value: 'RECHAZADA', label: 'Rechazar' }]} />
            {decision === 'APROBADA' && <Select label="Horas a autorizar" value={String(minutes)} onChange={(e) => setMinutes(Number(e.target.value))} options={minuteOptions} />}
            <Textarea label={decision === 'RECHAZADA' ? 'Motivo del rechazo' : 'Comentario (opcional)'} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={confirmSend}
        title="¿Enviar a Recursos Humanos?"
        description={`Se enviarán ${counts.ready} solicitud(es) de horas extras ya autorizadas. Después no podrás cambiarlas.`}
        confirmLabel="Sí, enviar"
        onConfirm={send}
        onCancel={() => setConfirmSend(false)}
        isLoading={busy}
      />
    </div>
  );
}
